/**
 * Unified Error Types for SportStack
 * ===================================
 * Standardized error handling for all frontend components.
 * 
 * USAGE:
 *   import { ApiErrorResponse, ErrorCategory } from '@/app/types/errors';
 *   import ErrorAlert from '@/app/components/ErrorAlert';
 *   
 *   <ErrorAlert error={apiError} onRetry={handleRetry} />
 */

/**
 * Machine-readable error categories matching backend ErrorCategory enum
 */
export enum ErrorCategory {
  VALIDATION = 'VALIDATION_ERROR',
  AUTHENTICATION = 'AUTHENTICATION_ERROR',
  AUTHORIZATION = 'AUTHORIZATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  DATABASE = 'DATABASE_ERROR',
  PLUGIN = 'PLUGIN_ERROR',
  EXTERNAL_SERVICE = 'EXTERNAL_SERVICE_ERROR',
  INTERNAL = 'INTERNAL_SERVER_ERROR',
  NETWORK = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT_ERROR',
}

/**
 * Individual field validation error
 */
export interface FieldError {
  field: string;
  message: string;
  error_type?: string;
  value?: unknown;
  /** Il passo del modulo da riaprire per correggere questo campo, deciso
   * dal backend (es. il passo-passo del link del giocatore). */
  step_code?: string;
  /** Il motivo, testo del backend (di solito uguale a `message`). */
  reason?: string;
}

/**
 * Standardized API error response from backend
 * All API errors use this format for consistency
 */
export interface ApiErrorResponse {
  error_code: ErrorCategory;
  message: string;
  technical_details?: string;
  field_errors?: FieldError[];
  /** TUTTI i motivi di un rifiuto, uno per voce, testo del backend: quando
   * c'e', `ErrorAlert` li elenca al posto del messaggio unito. */
  reasons?: string[];
  timestamp: string;
  request_id?: string;
  path?: string;
  method?: string;
  stack_trace?: string;
  help_url?: string;
}

/**
 * Props for ErrorAlert component
 */
export interface ErrorAlertProps {
  /** Error to display - supports multiple formats */
  error: ApiErrorResponse | Error | string | null;
  /** Optional title override */
  title?: string;
  /** Show technical details (default: only in dev) */
  showTechnicalDetails?: boolean;
  /** Retry callback */
  onRetry?: () => void;
  /** Dismiss callback */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** MUI Alert severity override */
  severity?: 'error' | 'warning' | 'info' | 'success';
}

/**
 * Type guard to check if error is ApiErrorResponse
 */
export function isApiErrorResponse(error: unknown): error is ApiErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error_code' in error &&
    'message' in error
  );
}

/**
 * Map error category to user-friendly Italian message
 */
export function getErrorCategoryMessage(category: ErrorCategory): string {
  const messages: Record<ErrorCategory, string> = {
    [ErrorCategory.VALIDATION]: 'Errore di validazione',
    [ErrorCategory.AUTHENTICATION]: 'Autenticazione richiesta',
    [ErrorCategory.AUTHORIZATION]: 'Accesso non autorizzato',
    [ErrorCategory.NOT_FOUND]: 'Risorsa non trovata',
    [ErrorCategory.CONFLICT]: 'Conflitto con dati esistenti',
    [ErrorCategory.DATABASE]: 'Errore database',
    [ErrorCategory.PLUGIN]: 'Errore plugin',
    [ErrorCategory.EXTERNAL_SERVICE]: 'Servizio esterno non disponibile',
    [ErrorCategory.INTERNAL]: 'Errore interno del server',
    [ErrorCategory.NETWORK]: 'Errore di rete',
    [ErrorCategory.TIMEOUT]: 'Timeout della richiesta',
  };
  return messages[category] || 'Errore sconosciuto';
}

/**
 * Map HTTP status code to ErrorCategory
 */
export function mapStatusToCategory(status: number): ErrorCategory {
  if (status === 400 || status === 422) return ErrorCategory.VALIDATION;
  if (status === 401) return ErrorCategory.AUTHENTICATION;
  if (status === 403) return ErrorCategory.AUTHORIZATION;
  if (status === 404) return ErrorCategory.NOT_FOUND;
  if (status === 409) return ErrorCategory.CONFLICT;
  if (status === 504) return ErrorCategory.TIMEOUT;
  if (status >= 500) return ErrorCategory.INTERNAL;
  return ErrorCategory.INTERNAL;
}
